import React from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import "./bugBounty.css";

const BugBounty = () => {
  return (
    <>
      <Helmet>
        <title>Security &amp; Resilience – AGCX</title>
        <meta
          name="description"
          content="Help us strengthen the security and resilience of AGCX. Join our bug bounty program and report vulnerabilities responsibly."
        />
        <meta
          name="keywords"
          content="AGCX security, bug bounty, vulnerability reporting, exchange resilience, ethical hacking"
        />
      </Helmet>

      <div className="bugbounty_page">
        {/* Section 1: Hero Section */}
        <section className="security_resilience_hero_sec">
          <div className="container">
            <h1 className="sec_title">
              Help us strengthen the security and <br className="d-none d-md-inline" />
              resilience of <span className="gold_text">AGCX</span>
            </h1>
            <p className="sec_subtitle">
              Security is fundamental to protecting our users, their assets, and the integrity of our platform. We welcome researchers who
              responsibly report vulnerabilities, and we're committed to investigating and addressing every eligible report.
            </p>

            <div className="herobnr_btn d-flex">
              <button className="reportbtn">Report a vulnerability <i class="ri-arrow-right-line"></i></button>
              <button>View scope</button>
            </div>

            <div className="hero_img_wrap">
              <img className="light_img" src="/images/security-resilience-img/security_resilience_hero_img.png" alt="Security &amp; Resilience Hero" />
              <img className="dark_img" src="/images/security-resilience-img/security_resilience_hero_img_light.png" alt="Security &amp; Resilience Hero" />
            </div>
          </div>
        </section>

        {/* Section 2: Our Commitment */}
        <section className="our_commitment_sec">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-7 col-md-6 mb-4 mb-md-0">
                <h2 className="sec_title">Our commitment</h2>
                <p className="commitment_text">
                  We continuously assess and improve the security of our infrastructure, applications, and processes.
                  This program gives independent researchers a direct, structured channel to our security team — so issues
                  reach the people who can act on them, quickly and
                  confidentially. We appreciate the time and expertise behind every report we receive.
                </p>
              </div>
              <div className="col-lg-5 col-md-6 text-center">
                <div className="commitment_img_wrap">
                  <img
                    src="/images/security-resilience-img/commitment_vector.png"
                    alt="Our Commitment"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Scope */}
        <section className="scope_sec">
          <div className="container">
            <h2 className="sec_title h2titlesope">Scope</h2>
            <p className="sec_subtitle">
              This program applies only to AGCX systems and assets explicitly listed below.
              Testing anything outside this list isn't permitted without prior written authorization.
            </p>

            <div className="scope_grid">
              {/* In Scope */}
              <div className="scope_card inscope">
                <div className="scope_card_header">
                  <div className="scope_icon_badge in_scope">
                    <i className="ri-check-line"></i>
                  </div>
                  <div className="sopecnt_in">
                    <h3 className="scope_card_title">In <span>scope</span></h3>
                    <ul className="scope_list">
                      <li>
                        app.arabglobal.ae — web platform
                      </li>
                      <li>
                        api.arabglobal.ae — public APIs
                      </li>
                      <li>
                        AGCX iOS & Android apps
                      </li>
                      <li>
                        Authentication & account management
                      </li>
                      <li>
                        Assets named in the detailed scope
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* Out of Scope */}
              <div className="scope_card">
                <div className="scope_card_header">
                  <div className="scope_icon_badge out_scope">
                    <i className="ri-close-line"></i>
                  </div>
                  <div className="sopecnt_in">
                    <h3 className="scope_card_title">Out of <span>scope</span></h3>
                    <ul className="scope_list">
                      <li>
                        Third-party vendor infrastructure
                      </li>
                      <li>
                        Corporate & marketing sites
                      </li>
                      <li>
                        Systems not explicitly listed
                      </li>
                      <li>
                        Physical offices & personnel
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Qualifying Issues */}
        <section className="qualifying_issues_sec">
          <div className="container">
            <h2 className="sec_title h2titlesope">Qualifying issues</h2>
            <p className="sec_subtitle">
              We're interested in vulnerabilities that could materially affect the confidentiality, integrity, or availability of AGCX systems or user information. Reports should demonstrate genuine impact and include enough detail for us to reproduce the issue.
            </p>

            <div className="qualifying_grid">
              <div className="qualifying_card activeeffect">
                <div className="qualifying_card_top">
                  <p className="qualifying_card_subtext">
                    Authentication bypass, account takeover, and privilege
                    escalation.
                  </p>
                  <div className="qualifying_check_icon">
                    <i class="ri-arrow-right-up-long-line"></i>
                  </div>
                </div>
                <h3 className="qualifying_card_title">Access & identity</h3>
              </div>

              <div className="qualifying_card ">
                <div className="qualifying_card_top">
                  <p className="qualifying_card_subtext">
                    Sensitive data exposure, insecure API endpoints, and insecure direct object references.
                  </p>
                  <div className="qualifying_check_icon">
                    <i class="ri-arrow-right-up-long-line"></i>
                  </div>
                </div>
                <h3 className="qualifying_card_title">Data exposure</h3>
              </div>

              <div className="qualifying_card">
                <div className="qualifying_card_top">
                  <p className="qualifying_card_subtext">
                    Cross-site scripting, SQL injection and related injection flaws, security misconfigurations
                  </p>
                  <div className="qualifying_check_icon">
                    <i class="ri-arrow-right-up-long-line"></i>
                  </div>
                </div>
                <h3 className="qualifying_card_title">Application layer</h3>
              </div>

              <div className="qualifying_card activeeffect">
                <div className="qualifying_card_top">
                  <p className="qualifying_card_subtext">
                    Business logic flaws and cryptographic weaknesses that could lead to unauthorized access.
                  </p>
                  <div className="qualifying_check_icon">
                    <i class="ri-arrow-right-up-long-line"></i>
                  </div>
                </div>
                <h3 className="qualifying_card_title">Platform logic</h3>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Rules of Engagement */}
        <section className="rules_of_engagement_sec">
          <div className="container">
            <h2 className="sec_title h2titlesope">
              Rules of <span className="gold_text">Engagement</span>
            </h2>
            <p className="sec_subtitle">
              To protect our users and infrastructure, the following actions are not permitted
              under this program:
            </p>

            <div className="rules_grid">
              <div className="rule_pill_card">
                Denial-of-service or DDoS testing
              </div>
              <div className="rule_pill_card">
                Spam, phishing, or social engineering
              </div>
              <div className="rule_pill_card">
                Physical attacks on AGCX facilities or people
              </div>
              <div className="rule_pill_card">
                Testing third-party systems without authorization
              </div>
              <div className="rule_pill_card">
                Accessing or exfiltrating another user's data
              </div>
              <div className="rule_pill_card">
                High-volume automated scanning
              </div>
              <div className="rule_pill_card">
                Trading or moving assets to prove a finding
              </div>
              <div className="rule_pill_card">
                Destructive testing or data modification
              </div>
            </div>
          </div>
        </section>

        {/* Section 6: Responsible Disclosure & Reporting */}
        <section className="responsible_disclosure_sec">
          <div className="container">
            <h2 className="sec_title h2titlesope">Responsible disclosure &amp; reporting</h2>
            <p className="sec_subtitle mx-auto">
              Please follow these principles when you find and report a vulnerability:
            </p>

            <div className="reporting_steps_row">
              <div className="reporting_step_item">
                <div className="reporting_icon_wrap">
                  <img
                    src="/images/security-resilience-img/reporting_vector.png"
                    alt="Test responsibly"
                  />
                </div>
                <h4 className="reporting_step_name">Test responsibly</h4>
                <p className="reporting_step_desc">
                  Stay within listed scope and avoid anything that could affect other users or services.
                </p>
              </div>

              <div className="reporting_step_item">
                <div className="reporting_icon_wrap">
                  <img
                    src="/images/security-resilience-img/reporting_vector2.png"
                    alt="Minimize impact"
                  />
                </div>
                <h4 className="reporting_step_name">Minimize impact</h4>
                <p className="reporting_step_desc">
                  Use the least intrusive method that still demonstrates the issue.
                </p>
              </div>

              <div className="reporting_step_item">
                <div className="reporting_icon_wrap">
                  <img
                    src="/images/security-resilience-img/reporting_vector3.png"
                    alt="Protect User Data"
                  />
                </div>
                <h4 className="reporting_step_name">Protect User Data</h4>
                <p className="reporting_step_desc">
                  If you encounter personal data, stop, don't retain it, and tell us immediately.
                </p>
              </div>

              <div className="reporting_step_item">
                <div className="reporting_icon_wrap">
                  <img
                    src="/images/security-resilience-img/reporting_vector4.png"
                    alt="Give us time"
                  />
                </div>
                <h4 className="reporting_step_name">Give us time</h4>
                <p className="reporting_step_desc">
                  Allow a reasonable window to investigate and remediate before going public.
                </p>
              </div>

              <div className="reporting_step_item">
                <div className="reporting_icon_wrap">
                  <img
                    src="/images/security-resilience-img/reporting_vector5.png"
                    alt="Keep it private"
                  />
                </div>
                <h4 className="reporting_step_name">Keep it private</h4>
                <p className="reporting_step_desc">
                  Don't disclose a vulnerability or its details without our written sign-off.
                </p>
              </div>
            </div>

            {/* Disclosure CTA Banner */}
            <div className="disclosure_banner">

              <div className="disclosure_info leftinfo_disclosure">
                <div className="disclosure_info_icon">
                  <i class="ri-mail-line"></i>
                </div>
                <div>
                  <h4><span>To</span> <a href="mailto:security@arabglobal.ae">security@arabglobal.ae</a></h4>
                  <p>
                    Subject: Bug Bounty Report — [brief description]
                  </p>
                </div>
              </div>


              <div className="disclosure_info listinfo_bl">

                <div className="disclosure_list">
                  <ul>
                    <li>Clear description of the vulnerability</li>
                    <li>Affected asset or URL</li>
                    <li>Steps to reproduce</li>
                    <li>Proof of concept, where
                      appropriate</li>
                  </ul>
                </div>

                <div className="disclosure_list">
                  <ul>
                    <li>Potential security impact</li>
                    <li>Supporting evidence
                      (screenshots, logs)</li>
                    <li>Suggested remediation, if you
                      have one</li>
                    <li>No unnecessary personal or
                      confidential data</li>
                  </ul>
                </div>

              </div>
              <a href="mailto:security@agcx.io" className="disclosure_btn">
                Report a vulnerability
              </a>
            </div>
          </div>
        </section>

        {/* Section 7: What Happens Next */}
        <section className="what_happens_next_sec">
          <div className="container">
            <h2 className="sec_title h2titlesope">What happens next</h2>
            <p className="sec_subtitle mx-auto">
              Timelines vary with severity and complexity, but every report moves through the same five stages:
            </p>

            <div className="what_next_grid">
              <div className="next_card">
                <img className="card_bg_img light_img" src="/images/security-resilience-img/security_happiness_back.png" alt="back" />
                <img className="card_bg_img dark_img" src="/images/security-resilience-img/security_happiness_back_light.png" alt="back" />

                <div className="happens_cardcnt">

                  <div className="next_card_icon">
                    <img className="light_img" src="/images/security-resilience-img/submission_vector.png" alt="Submission" />
                    <img className="dark_img" src="/images/security-resilience-img/submission_vector_light.png" alt="Submission" />
                  </div>
                  <h4 className="next_card_title">Submission</h4>
                  <p className="next_card_desc">You report through our
                    designated channel.</p>

                </div>

              </div>

              <div className="next_card">
                <img className="card_bg_img light_img" src="/images/security-resilience-img/security_happiness_back.png" alt="back" />
                <img className="card_bg_img dark_img" src="/images/security-resilience-img/security_happiness_back_light.png" alt="back" />

                <div className="happens_cardcnt">

                  <div className="next_card_icon">
                    <img className="light_img"
                      src="/images/security-resilience-img/acknowledgement_vector.png"
                      alt="Acknowledgement"
                    />
                    <img className="dark_img" src="/images/security-resilience-img/acknowledgement_vector_light.png" alt="Acknowledgement" />
                  </div>
                  <h4 className="next_card_title">Acknowledgement</h4>
                  <p className="next_card_desc">We confirm receipt of your report.</p>
                </div>
              </div>

              <div className="next_card">
                <img className="card_bg_img light_img" src="/images/security-resilience-img/security_happiness_back.png" alt="back" />
                <img className="card_bg_img dark_img" src="/images/security-resilience-img/security_happiness_back_light.png" alt="back" />

                <div className="happens_cardcnt">

                  <div className="next_card_icon">
                    <img className="light_img" src="/images/security-resilience-img/validation_vector.png"
                      alt="Validation"
                    />
                    <img className="dark_img" src="/images/security-resilience-img/validation_vector_light.png" alt="Validation" />
                  </div>
                  <h4 className="next_card_title">Validation</h4>
                  <p className="next_card_desc">Our team confirms impact and scope</p>
                </div>
              </div>

              <div className="next_card">
                <img className="card_bg_img light_img" src="/images/security-resilience-img/security_happiness_back.png" alt="back" />
                <img className="card_bg_img dark_img" src="/images/security-resilience-img/security_happiness_back_light.png" alt="back" />

                <div className="happens_cardcnt">

                  <div className="next_card_icon">
                    <img className="light_img"
                      src="/images/security-resilience-img/remediation_vector.png"
                      alt="Remediation"
                    />
                    <img className="dark_img" src="/images/security-resilience-img/remediation_vector_light.png" alt="Remediation" />
                  </div>
                  <h4 className="next_card_title">Remediation</h4>
                  <p className="next_card_desc">We work to fix or mitigate the issue.</p>
                </div>
              </div>

              <div className="next_card">
                <img className="card_bg_img light_img" src="/images/security-resilience-img/security_happiness_back.png" alt="back" />
                <img className="card_bg_img dark_img" src="/images/security-resilience-img/security_happiness_back_light.png" alt="back" />
                <div className="happens_cardcnt">
                  <div className="next_card_icon">
                    <img className="light_img" src="/images/security-resilience-img/resolution_vector.png" alt="Resolution" />
                    <img className="dark_img" src="/images/security-resilience-img/resolution_vector_light.png" alt="Resolution" />
                  </div>
                  <h4 className="next_card_title">Resolution</h4>
                  <p className="next_card_desc">The report closes
                    once addressed.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8: Rewards */}
        <section className="rewards_sec">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 col-md-12 mb-4 mb-lg-0">
                <h2 className="sec_title h2titlesope">Rewards</h2>
                <p className="commitment_text">
                  Rewards reflect severity, security impact, report quality, and how easily the issue could be
                  exploited. Bands below are indicative — final
                  amounts are set by our security team on a case-by-case basis.
                </p>
                <div className="rewards_callout_box">
                  <i className="ri-information-fill"></i>
                  <span>Duplicate reports, previously known issues, and findings outside this program's scope don't qualify for a reward.</span>
                </div>
              </div>

              <div className="col-lg-6 col-md-12">
                <div className="rewards_card">
                  <div className="reward_row rangelevel">
                    <span className="reward_level">
                      Sevrity
                    </span>
                    <span className="reward_val">Indicative Range</span>
                  </div>


                  <div className="reward_row">
                    <span className="reward_level">
                      <i class="ri-circle-fill critical"></i> Critical

                    </span>
                    <span className="reward_val dangercolor">AED 7,500 – 10,000</span>
                  </div>
                  <div className="reward_row">
                    <span className="reward_level">
                      <i class="ri-circle-fill high"></i> High
                    </span>
                    <span className="reward_val">AED 3,000 – 7,500</span>
                  </div>
                  <div className="reward_row">
                    <span className="reward_level">
                      <i class="ri-circle-fill medium"></i> Medium
                    </span>
                    <span className="reward_val">AED 1,000 – 3,000</span>
                  </div>
                  <div className="reward_row">
                    <span className="reward_level">
                      <i class="ri-circle-fill low"></i> Low
                    </span>
                    <span className="reward_val">AED 250 – 1,000</span>
                  </div>
                  <div className="reward_row">
                    <span className="reward_level">
                      <i class="ri-circle-fill information"></i> Informational
                    </span>
                    <span className="reward_val">Recognition, no monetary reward</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 9: Safe Harbor */}
        <section className="safe_harbor_sec">
          <div className="container">
            <div className="safe_harbor_main_card">
              <div className="safe_harbor_left">
                <h2 className="sec_title h2titlesope">Safe harbor</h2>
                <p className="safe_harbor_desc">
                  AGCX supports good-faith security research conducted under this program. Where a researcher acts
                  in good faith, follows these rules, avoids intentional harm, respects user privacy, and reports issues
                  promptly, we'll consider that research authorized — and won't pursue legal action for activity within the
                  program's permitted scope.
                </p>
              </div>

              <div className="safe_harbor_notice_card">
                <div className="safe_notice_icon">
                  <i className="ri-shield-line"></i>
                </div>
                <p className="safe_notice_text">
                  This commitment doesn't extend to activity that breaks the law, causes intentional harm, misuses
                  user data, disrupts our services, or falls outside the program's scope.
                </p>
              </div>
            </div>

            <div className="safe_harbor_bottom_grid">
              <div className="safe_mini_card">
                <div className="safe_mini_content">
                  <h4 className="safe_mini_title questionshd">Questions?</h4>
                  <p className="safe_mini_desc">
                    If you're unsure whether an activity is permitted, ask before you test.
                  </p>
                </div>
              </div>

              <div className="safe_mini_card">
                <div className="safe_mini_icon">
                  <i className="ri-mail-line"></i>
                </div>
                <div className="safe_mini_content">
                  <h4 className="safe_mini_title">security@arabglobal.ae</h4>
                  <p className="safe_mini_desc">
                    Our security team responds to scope and eligibility questions directly.
                  </p>
                </div>
              </div>

              <div className="safe_mini_card">
                <div className="safe_mini_icon">
                  <i className="ri-star-line"></i>
                </div>
                <div className="safe_mini_content">
                  <h4 className="safe_mini_title">With your permission, we may credit</h4>
                  <p className="safe_mini_desc">
                    researchers who responsibly disclose eligible vulnerabilities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default BugBounty;
